// SendEmailModal.js - Modal för att skicka email till patient
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../UserContext';
import apiClient from '../../apiClient';
import TextAreaWithPhrases from '../TextAreaWithPhrases';
import './SendEmailModal.css';

const SendEmailModal = ({ patient, onClose, onSuccess }) => {
  const { user } = useContext(UserContext);
  const useInternalJournal = user?.clinic?.use_internal_journal || false;

  const [email, setEmail] = useState(patient?.email || '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Hämta email-mallar
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const response = await apiClient.get('/api/email/templates');
        setTemplates(response.data || []);
      } catch (err) {
        console.error('Error fetching email templates:', err);
        // Inte kritiskt - användaren kan fortfarande skriva fritext
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  // Hantera mallval
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);

    if (templateId) {
      const template = templates.find(t => t.template_id.toString() === templateId);
      if (template) {
        setSubject(template.subject || '');
        setMessage(template.body || '');
      }
    }
  };

  // Skicka email
  const handleSend = async () => {
    if (!email.trim()) {
      setError('Ange en email-adress');
      return;
    }

    if (!subject.trim()) {
      setError('Ange ett ämne');
      return;
    }

    if (!message.trim()) {
      setError('Skriv ett meddelande');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiClient.post('/api/email/send', {
        patient_id: patient.patient_id,
        email: email,
        subject: subject,
        message: message,
        template_id: selectedTemplate || null
      });

      setSuccess(`Email skickat till ${email}`);

      // Stäng efter kort delay
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error sending email:', err);
      setError(err.response?.data?.msg || 'Kunde inte skicka email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="email-modal-overlay" onClick={onClose}>
      <div className="email-modal" onClick={e => e.stopPropagation()}>
        <div className="email-modal-header">
          <h3>
            <i className="fas fa-envelope"></i> Skicka email
          </h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="email-modal-body">
          {/* Patient info */}
          <div className="patient-info">
            <span className="patient-name">
              {patient.firstname} {patient.lastname}
            </span>
            <span className="patient-pnr">{patient.personnummer}</span>
          </div>

          {/* Email-adress */}
          <div className="form-group">
            <label>
              <i className="fas fa-at"></i> Email-adress
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="patient@example.com"
              disabled={loading}
            />
          </div>

          {/* Mall-dropdown */}
          <div className="form-group">
            <label>
              <i className="fas fa-file-alt"></i> Mall (valfritt)
            </label>
            <select
              value={selectedTemplate}
              onChange={handleTemplateChange}
              disabled={loading || loadingTemplates}
            >
              <option value="">-- Välj mall eller skriv fritext --</option>
              {templates.map(template => (
                <option key={template.template_id} value={template.template_id}>
                  {template.template_name}
                </option>
              ))}
            </select>
          </div>

          {/* Ämne */}
          <div className="form-group">
            <label>
              <i className="fas fa-heading"></i> Ämne
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ange ämne..."
              disabled={loading}
            />
          </div>

          {/* Meddelande */}
          <div className="form-group">
            <label>
              <i className="fas fa-comment"></i> Meddelande
            </label>
            {useInternalJournal ? (
              <TextAreaWithPhrases
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Skriv ditt meddelande här..."
                rows={8}
                disabled={loading}
              />
            ) : (
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Skriv ditt meddelande här..."
                rows={8}
                disabled={loading}
              />
            )}
          </div>

          {/* Variabler-hjälp */}
          <div className="variables-help">
            <details>
              <summary>
                <i className="fas fa-info-circle"></i> Tillgängliga variabler
              </summary>
              <div className="variables-list">
                <code>{'{firstname}'}</code> - Förnamn<br />
                <code>{'{lastname}'}</code> - Efternamn<br />
                <code>{'{patientnamn}'}</code> - Fullständigt namn<br />
                <code>{'{kliniknamn}'}</code> - Klinikens namn<br />
                <code>{'{behandlare}'}</code> - Behandlarens namn
              </div>
            </details>
          </div>

          {/* Felmeddelande */}
          {error && (
            <div className="email-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="email-success">
              <i className="fas fa-check-circle"></i> {success}
            </div>
          )}
        </div>

        <div className="email-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Avbryt
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={loading || !email.trim() || !subject.trim() || !message.trim()}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Skickar...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i> Skicka email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
